/**
 * 用一段话描述产品 → AI 解析成草稿 → 进入向导确认。
 * 解析结果永远不直接落库，管理员必须在向导里过一遍再保存。
 */
import { useState } from "react";
import { Sparkles, PenLine } from "lucide-react";
import { Alert, Button, Card, CardContent, CardFooter, Textarea } from "@/components/ui";
import { ApiError } from "@/lib/api-client";
import * as api from "../api";
import type { WizardDraft } from "./serializers";

const EXAMPLE = "KT板5mm厚，按面积算，5平米以内每平米35元，超过5平米每平米30元，最低起价50元，双面加价1.6倍。客户一般叫它KT板或kt。";

interface Props {
  onParsed: (draft: WizardDraft, notes: string[]) => void;
  onManual: () => void;
}

export function DescribeStep({ onParsed, onManual }: Props) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const parse = async () => {
    setErr(null);
    setLoading(true);
    try {
      const res = await api.parseProductDescription(text);
      onParsed(res.draft, res.notes ?? []);
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? ((e.body as { detail?: string } | null)?.detail ?? `解析失败 (${e.status})`)
          : "解析失败，请稍后重试",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="overflow-hidden">
      <div className="border-b bg-primary-soft/60 px-4 py-4 sm:px-5">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <h2 className="text-section">用一段话描述产品</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          把价格规则按平时跟客户说的方式写出来，AI 会填好表单，你确认后再保存。
        </p>
      </div>

      <CardContent className="space-y-3">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          maxLength={2000}
          aria-label="产品描述"
          placeholder={`例如：${EXAMPLE}`}
          className="resize-y"
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="link" size="sm" onClick={() => setText(EXAMPLE)} className="text-xs">
            填入示例看看效果
          </Button>
          <span className="text-xs tabular-nums text-muted-foreground">{text.length}/2000</span>
        </div>

        {err && <Alert>{err}</Alert>}
      </CardContent>

      <CardFooter className="justify-between">
        <Button variant="outline" icon={<PenLine />} onClick={onManual}>手动填写</Button>
        <Button icon={<Sparkles />} loading={loading} disabled={!text.trim()} onClick={parse}>
          {loading ? "解析中…" : "AI 解析"}
        </Button>
      </CardFooter>
    </Card>
  );
}
